<template>
  <b-card>
    <!-- زر فتح السجل -->
    <b-button
      variant="primary"
      class="mb-75 mx-1"
      @click="openAuditModal"
    >
      <feather-icon
        icon="FileTextIcon"
        class="ml-50"
      />
      عرض سجل التدقيق
    </b-button>

    <!-- النافذة المنبثقة -->
    <b-modal
      id="audit-log-modal"
      size="xl"
      title="سجل تدقيق العملية"
      hide-footer
      body-class="p-0"
      :dir="'rtl'"
    >
      <!-- Loading -->
      <div
        v-if="loading"
        class="text-center py-4"
      >
        <b-spinner small />
        <small class="text-muted d-block mt-1">جاري التحميل...</small>
      </div>

      <!-- Logs -->
      <div
        v-else-if="auditLogs.length"
        class="audit-list"
      >

        <div
          v-for="log in auditLogs"
          :key="log.id"
          class="audit-row"
        >
          <!-- Header -->
          <div class="audit-row-header">
            <div class="left">
              <b-badge
                :variant="getEventVariant(log.event)"
                pill
              >
                {{ formatEvent(log.event) }}
              </b-badge>

              <span class="label">
                {{ log.label || extractModel(log.auditable_type) }}
              </span>
            </div>

            <div class="right">
              <small class="date">
                {{ formatDate(log.created_at) }}
              </small>
            </div>
          </div>

          <!-- Meta -->
          <div class="audit-row-meta">
            <span>👤 {{ getUserName(log) }}</span>
            <!-- <span v-if="log.url">🔗 {{ log.url }}</span> -->
          </div>

          <!-- Changes -->
          <div
            v-if="hasChanges(log)"
            class="audit-row-changes"
          >

            <!-- Created -->
            <template v-if="log.event === 'created'">
              <div
                v-for="(value, key) in getFilteredValues(log.new_values)"
                :key="'c-' + key"
                class="change-item created"
              >
                <span class="field">{{ formatField(key) }}</span>
                <span class="value">{{ formatValue(value) }}</span>
              </div>
            </template>

            <!-- Updated -->
            <template v-else-if="log.event === 'updated'">
              <div
                v-for="key in getChangedKeys(log)"
                :key="'u-' + key"
                class="change-item updated"
              >
                <span class="field">{{ formatField(key) }}</span>

                <span class="old">
                  {{ formatValue(log.old_values[key]) }}
                </span>

                <span class="arrow">→</span>

                <span class="new">
                  {{ formatValue(log.new_values[key]) }}
                </span>
              </div>
            </template>

            <!-- Deleted -->
            <template v-else-if="log.event === 'deleted'">
              <div
                v-for="(value, key) in getFilteredValues(log.old_values)"
                :key="'d-' + key"
                class="change-item deleted"
              >
                <span class="field">{{ formatField(key) }}</span>
                <span class="value">{{ formatValue(value) }}</span>
              </div>
            </template>

          </div>

          <!-- No Changes -->
          <div
            v-else
            class="audit-row-no-changes"
          >
            لا توجد تغييرات مرئية
          </div>
        </div>
      </div>

      <!-- Empty -->
      <div
        v-else
        class="text-center py-4"
      >
        <small class="text-muted">لا توجد بيانات في سجل التدقيق</small>
      </div>
    </b-modal>

  </b-card>
</template>

<script>
export default {
  name: 'PatientSurgeryAuditLog',

  props: {
    item: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      loading: false,
      auditLogs: [],
      loadedOnce: false,
    }
  },

  methods: {
    async openAuditModal() {
      this.$bvModal.show('audit-log-modal')

      if (!this.loadedOnce) {
        await this.fetchAuditLog()
        this.loadedOnce = true
      }
    },

    async fetchAuditLog() {
      this.loading = true

      try {
        await this.getList(`/patient-surgery/${this.item.id}/audit-log`)

        this.auditLogs = (this.all_data
                && this.all_data.data
                && this.all_data.data.audit_log)
              || []
      } catch (error) {
        console.error('خطأ في جلب سجل التدقيق:', error)
        this.auditLogs = []
      } finally {
        this.loading = false
      }
    },

    // التحقق من وجود تغييرات
    hasChanges(log) {
      const oldValues = this.getFilteredValues(log.old_values || {})
      const newValues = this.getFilteredValues(log.new_values || {})
      return Object.keys(oldValues).length > 0 || Object.keys(newValues).length > 0
    },

    // الحصول على المفاتيح المتغيرة
    getChangedKeys(log) {
      const oldValues = log.old_values || {}
      const newValues = log.new_values || {}

      const allKeys = new Set([
        ...Object.keys(oldValues),
        ...Object.keys(newValues),
      ])

      return Array.from(allKeys).filter(key => this.isVisibleField(key, oldValues[key], newValues[key]))
    },

    // تصفية القيم من UUIDs
    getFilteredValues(values) {
      if (!values || typeof values !== 'object') return {}

      const filtered = {}
      Object.keys(values).forEach(key => {
        if (this.isVisibleField(key, values[key])) {
          filtered[key] = values[key]
        }
      })
      return filtered
    },

    // التحقق من الحقول المرئية
    isVisibleField(key, value) {
      // إخفاء الـ IDs
      if (key.toLowerCase() === 'id') return false
      if (key.toLowerCase().endsWith('_id')) return false
      if (key.toLowerCase().includes('uuid')) return false

      // إخفاء القيم التي هي UUIDs
      if (this.isUUID(value)) return false

      return true
    },

    // التحقق من UUID
    isUUID(val) {
      if (!val) return false
      return (
        typeof val === 'string'
            && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(val)
      )
    },

    // الحصول على اسم المستخدم
    getUserName(log) {
      if (log.user && log.user.name) return log.user.name
      if (log.user_id) return `مستخدم #${log.user_id.substring(0, 8)}`
      return 'النظام'
    },

    // تنسيق أسماء الحقول
    formatField(key) {
      const translations = {
        name: 'الاسم',
        status: 'الحالة',
        date: 'التاريخ',
        time: 'الوقت',
        price: 'السعر',
        discount: 'الخصم',
        amount: 'المبلغ',
        doctor: 'الطبيب',
        doctor_price: 'سعر الطبيب',
        doctor_discount: 'خصم الطبيب',
        surgery_discount: 'خصم العملية',
        payment_status: 'حالة الدفع',
        payment_date: 'تاريخ الدفع',
        type: 'النوع',
        notes: 'الملاحظات',
        description: 'الوصف',
        surgery_date_time: 'تاريخ ووقت العملية',
        money_collected: 'تم التحصيل',
        created_by: 'أنشئ بواسطة',
        updated_by: 'حدث بواسطة',
        approved_by: 'وافق عليه',
        approved_at: 'تاريخ الموافقة',
        created_at: 'تاريخ الإنشاء',
        updated_at: 'تاريخ التحديث',
        deleted_at: 'تاريخ الحذف',
        patient_card_surgery_id: 'رقم سجل العملية',
      }

      const arabicKey = translations[key.toLowerCase()]
      if (arabicKey) return arabicKey

      return key
        .replace(/_/g, ' ')
        .replace(/\b\w/g, l => l.toUpperCase())
    },

    // تنسيق القيم
    formatValue(val) {
      if (val === null || val === undefined || val === '') return '—'
      if (val === true || val === 1) return 'نعم'
      if (val === false || val === 0) return 'لا'

      // التواريخ
      if (typeof val === 'string' && /^\d{4}-\d{2}-\d{2}/.test(val)) {
        return this.formatDate(val)
      }

      // الأرقام الكبيرة (المبالغ)
      if (typeof val === 'number' && val > 999) {
        return val.toLocaleString('ar-SA')
      }

      // الكائنات
      if (typeof val === 'object') {
        return JSON.stringify(val, null, 2)
      }

      return String(val)
    },

    // تنسيق نوع الحدث
    formatEvent(event) {
      const eventTranslations = {
        created: 'إنشاء',
        updated: 'تحديث',
        deleted: 'حذف',
        restored: 'استعادة',
      }

      if (!event) return '—'
      return eventTranslations[event.toLowerCase()] || event
    },

    // لون الحدث
    getEventVariant(event) {
      const variants = {
        created: 'success',
        updated: 'primary',
        deleted: 'danger',
        restored: 'warning',
      }
      return variants[event?.toLowerCase()] || 'secondary'
    },

    // استخراج اسم النموذج
    extractModel(type) {
      if (!type) return '—'

      const modelName = type.split('\\').pop()

      const translations = {
        PatientCardSurgery: 'سجل العملية الجراحية',
        DoctorPatientCardSurgery: 'ربط الطبيب بالعملية',
        Invoice: 'الفاتورة',
        Payment: 'الدفع',
      }

      return translations[modelName] || modelName
    },

    // تنسيق التاريخ
    formatDate(date) {
      if (!date) return '—'

      const dateObj = new Date(date)

      if (isNaN(dateObj.getTime())) return date

      const now = new Date()
      const diff = now - dateObj
      const hours = Math.floor(diff / (1000 * 60 * 60))
      const days = Math.floor(hours / 24)

      // عرض نسبي للتواريخ الحديثة
      if (hours < 1) {
        return 'منذ دقائق'
      } if (hours < 24) {
        return `منذ ${hours} ساعة`
      } if (days < 7) {
        return `منذ ${days} يوم`
      }

      return dateObj.toLocaleString('ar-SA', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        hour12: true,
      })
    },
  },
}
</script>

  <style lang="scss" scoped>
  // حاوية السجل
  .audit-container {
    max-height: 70vh;
    overflow-y: auto;
    direction: rtl;
  }

  // بطاقة التدقيق
  .audit-card {
    border: 1px solid #e9ecef;
    border-radius: 0.5rem;
    overflow: hidden;
    background: #fff;
    transition: all 0.3s ease;

    &:hover {
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
      transform: translateY(-2px);
    }
  }

  // رأس البطاقة
  .audit-header {
    background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
    padding: 1rem 1.25rem;
    border-bottom: 2px solid #dee2e6;

    h6 {
      font-size: 1rem;
      font-weight: 600;
      color: #2c3e50;
    }
  }

  // جسم البطاقة
  .audit-body {
    padding: 1.25rem;
  }

  // شبكة المعلومات
  .info-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 0.75rem;
  }

  .info-item {
    display: flex;
    align-items: center;
    padding: 0.5rem;
    background: #f8f9fa;
    border-radius: 0.25rem;

    &.full-width {
      grid-column: 1 / -1;
    }
  }

  .info-label {
    font-weight: 600;
    color: #495057;
    margin-left: 0.5rem;
    white-space: nowrap;
  }

  .info-value {
    color: #212529;
    word-break: break-word;

    &.small {
      font-size: 1rem;
      color: #6c757d;
    }
  }

  // عنوان القسم
  .section-title {
    font-size: 1rem;
    font-weight: 600;
    color: #495057;
    margin-bottom: 0.75rem;
    padding-bottom: 0.5rem;
    border-bottom: 1px solid #e9ecef;
  }

  // حاوية التغييرات
  .changes-container {
    background: #f8f9fa;
    border-radius: 0.375rem;
    padding: 0.75rem;
  }

  // صف التغيير (للإنشاء/الحذف)
  .change-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.5rem 0.75rem;
    margin-bottom: 0.5rem;
    border-radius: 0.25rem;

    &:last-child {
      margin-bottom: 0;
    }

    &.created {
      background: #d4edda;
      border-right: 3px solid #28a745;
    }

    &.deleted {
      background: #f8d7da;
      border-right: 3px solid #dc3545;
    }

    .change-field {
      font-weight: 600;
      color: #495057;
      flex: 0 0 40%;
    }

    .change-value {
      flex: 1;
      text-align: left;
      font-family: 'Courier New', monospace;
      font-size: 1rem;
      color: #212529;
    }
  }

  // مقارنة التغيير (للتحديث)
  .change-comparison {
    margin-bottom: 1rem;
    padding: 0.75rem;
    background: #fff;
    border-radius: 0.375rem;
    border: 1px solid #dee2e6;

    &:last-child {
      margin-bottom: 0;
    }

    .field-name {
      font-weight: 600;
      color: #2c3e50;
      margin-bottom: 0.5rem;
      font-size: 1rem;
    }

    .values-row {
      display: grid;
      grid-template-columns: 1fr auto 1fr;
      gap: 0.75rem;
      align-items: center;
    }

    .old-value,
    .new-value {
      padding: 0.5rem 0.75rem;
      border-radius: 0.25rem;
      font-size: 1rem;
    }

    .old-value {
      background: #fff3cd;
      border-right: 3px solid #ffc107;

      .value-label {
        display: block;
        font-size: 1rem;
        color: #856404;
        margin-bottom: 0.25rem;
        font-weight: 600;
      }

      .value-text {
        color: #212529;
        font-family: 'Courier New', monospace;
      }
    }

    .new-value {
      background: #d1ecf1;
      border-right: 3px solid #17a2b8;

      .value-label {
        display: block;
        font-size: 1rem;
        color: #0c5460;
        margin-bottom: 0.25rem;
        font-weight: 600;
      }

      .value-text {
        color: #212529;
        font-family: 'Courier New', monospace;
      }
    }

    .arrow {
      text-align: center;
      font-size: 1.5rem;
      color: #6c757d;
      font-weight: bold;
    }
  }

  // لا توجد تغييرات
  .no-changes {
    padding: 1.5rem;
    text-align: center;
    background: #f8f9fa;
    border-radius: 0.375rem;
    border: 1px dashed #dee2e6;
  }

  // Scrollbar
  .audit-container::-webkit-scrollbar {
    width: 6px;
  }

  .audit-container::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 10px;
  }

  .audit-container::-webkit-scrollbar-thumb {
    background: #7367f0;
    border-radius: 10px;

    &:hover {
      background: #5e50ee;
    }
  }

  // الشارات
  ::v-deep .badge {
    font-size: 1rem;
    padding: 0.35rem 0.65rem;
    font-weight: 600;
  }

  // Modal RTL
  ::v-deep .modal-header {
    direction: rtl;
    text-align: right;
  }

  ::v-deep .modal-body {
    padding: 0 !important;
  }

  // Responsive
  @media (max-width: 768px) {
    .info-grid {
      grid-template-columns: 1fr;
    }

    .change-comparison .values-row {
      grid-template-columns: 1fr;
      gap: 0.5rem;

      .arrow {
        transform: rotate(90deg);
      }
    }

    .change-row {
      flex-direction: column;
      align-items: flex-start;

      .change-field {
        margin-bottom: 0.25rem;
      }

      .change-value {
        text-align: right;
        width: 100%;
      }
    }
  }
  .audit-list {
  max-height: 85vh;
  overflow-y: auto;
  padding: 8px;
}

.audit-row {
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  padding: 8px 10px;
  margin-bottom: 8px;
  background: #fff;
  font-size: 0.8rem;
}

/* Header */
.audit-row-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 4px;

  .left {
    display: flex;
    align-items: center;
    gap: 6px;

    .label {
      font-weight: 600;
      color: #2c3e50;
    }
  }

  .date {
    font-size: 1rem;
    color: #6b7280;
  }
}

/* Meta */
.audit-row-meta {
  display: flex;
  gap: 12px;
  font-size: 1rem;
  color: #6b7280;
  margin-bottom: 6px;
}

/* Changes */
.audit-row-changes {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

/* Change item */
.change-item {
  display: grid;
  grid-template-columns: 140px 1fr auto 1fr;
  gap: 6px;
  align-items: center;
  font-size: 1rem;
  padding: 4px 6px;
  border-radius: 4px;
  background: #f9fafb;

  .field {
    font-weight: 600;
    color: #374151;
  }

  .old {
    color: #92400e;
    background: #fff7ed;
    padding: 2px 4px;
    border-radius: 3px;
  }

  .new {
    color: #065f46;
    background: #ecfdf5;
    padding: 2px 4px;
    border-radius: 3px;
  }

  .value {
    grid-column: 2 / -1;
  }

  .arrow {
    text-align: center;
    font-weight: bold;
    color: #9ca3af;
  }

  &.created {
    border-right: 3px solid #22c55e;
  }

  &.updated {
    border-right: 3px solid #3b82f6;
  }

  &.deleted {
    border-right: 3px solid #ef4444;
  }
}

/* No changes */
.audit-row-no-changes {
  font-size: 1rem;
  color: #9ca3af;
  padding: 4px 0;
}

  </style>
